import { useEffect, useState } from 'react';

const CSS_VAR = '--keyboard-inset-height';

function readKeyboardInsetPx(): number {
  if (typeof window === 'undefined') return 0;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(CSS_VAR)
    .trim();
  const next = Number.parseFloat(raw);
  return Number.isFinite(next) ? Math.max(0, next) : 0;
}

/** Live `--keyboard-inset-height` from `<html>` (Capacitor Keyboard.resize none). */
export function useCssKeyboardInset(active = true): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    const compute = () => {
      const next = readKeyboardInsetPx();
      setInset((prev) => (prev === next ? prev : next));
    };

    compute();
    const observer = new MutationObserver(compute);
    observer.observe(root, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [active]);

  return active ? inset : 0;
}

export function keyboardAwarePopoverCollision(inset: number): {
  top: number;
  left: number;
  right: number;
  bottom: number;
} {
  return { top: 12, left: 12, right: 12, bottom: 12 + inset };
}

const EDGE_GAP_PX = 12;
const NATIVE_TAB_BAR_PX = 88;

function readSafeAreaPadding(): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  if (typeof document === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;pointer-events:none;visibility:hidden;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  document.documentElement.append(probe);
  const style = getComputedStyle(probe);
  const padding = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return padding;
}

/** Keep popovers inside the status bar / floating tab bar on Capacitor. */
export function shellAwarePopoverCollision(): {
  top: number;
  left: number;
  right: number;
  bottom: number;
} {
  const inset = readSafeAreaPadding();
  const nativeTab =
    typeof document !== 'undefined' &&
    document.documentElement.hasAttribute('data-native-shell')
      ? NATIVE_TAB_BAR_PX
      : EDGE_GAP_PX;
  return {
    top: inset.top + EDGE_GAP_PX,
    right: inset.right + EDGE_GAP_PX,
    bottom: inset.bottom + nativeTab,
    left: inset.left + EDGE_GAP_PX,
  };
}
